import { AdminCurationService } from "../../../services/admin-curation.service.js";
import { ModalManager } from "../../../utils/ModalManager.js";
import { AlertModal } from "../../../components/modals/AlertModal.js";
import { ConfirmModal } from "../../../components/common/ConfirmModal.js";

export const CurationTab = async (container) => {
    container.innerHTML = '<div class="flex justify-center py-20"><span class="material-symbols-outlined animate-spin text-4xl text-gray-300">stars</span></div>';

    try {
        // [Step 1] Fetch Data via Service
        const [banners, curatedSeries, candidates] = await Promise.all([
            AdminCurationService.getBanners(),
            AdminCurationService.getCuratedSeries(),
            AdminCurationService.getCandidates(50)
        ]);

        // Helper to decode HTML entities (Robust & Recursive)
        const decodeHtml = (html) => {
            if (!html) return '';
            const txt = document.createElement("textarea");
            txt.innerHTML = html;
            let decoded = txt.value;
            // Prevent double encoding (e.g. &amp;#039;)
            // If the decoded string still contains entities, decode again (up to a limit)
            let limit = 0;
            while ((decoded.includes('&amp;') || decoded.includes('&#')) && limit < 3) {
                txt.innerHTML = decoded;
                decoded = txt.value;
                limit++;
            }
            return decoded;
        };

        const curatedIds = new Set(curatedSeries.map(s => s.id));

        // [Step 2] AI Analysis Logic (Utilizing Service Helpers)
        const risingStars = AdminCurationService.filterRisingStars(candidates, curatedIds);
        const hiddenGems = AdminCurationService.filterHiddenGems(candidates, curatedIds);

        // [Step 3] Render UI
        container.innerHTML = `
            <div class="space-y-8 animate-fade-in">
                
                <!-- 1. Main Banner Carousel -->
                <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div class="flex justify-between items-center mb-6 border-b pb-4">
                        <div>
                            <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <span class="material-symbols-outlined text-pink-500">collections_bookmark</span>
                                Main Carousel
                            </h2>
                            <p class="text-xs text-gray-500 mt-1">최대 5개의 배너가 메인 화면 최상단에 노출됩니다. (16:9 비율 자동 최적화)</p>
                        </div>
                        <button id="btn-add-banner" class="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-black transition-colors flex items-center gap-2">
                            <span class="material-symbols-outlined text-sm">add_photo_alternate</span> 배너 추가
                        </button>
                    </div>

                    <!-- Banner Grid -->
                    <div id="banner-list" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        ${banners.length === 0 ? '<div class="col-span-3 text-center py-10 text-gray-400">등록된 배너가 없습니다.</div>' : ''}
                    </div>
                </div>

                <!-- 2. Today's Picks (Priority Curation) -->
                <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div class="flex justify-between items-center mb-6 border-b pb-4">
                        <div>
                            <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <span class="material-symbols-outlined text-purple-500">recommend</span>
                                Today's Picks (큐레이션)
                            </h2>
                            <p class="text-xs text-gray-500 mt-1">오늘의 추천 섹션 노출 순서를 제어합니다. (낮은 번호가 먼저 노출)</p>
                        </div>
                        <button id="btn-add-curation" class="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors flex items-center gap-2">
                            <span class="material-symbols-outlined text-sm">playlist_add</span> 작품 수동 추가
                        </button>
                    </div>

                    <!-- Curated List -->
                    <div class="overflow-hidden border rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">Rank</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Series</th>
                                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody id="curation-list" class="bg-white divide-y divide-gray-200">
                                <!-- Rendered via JS -->
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- 2.5 Weekly Hall of Fame (Recovered) -->
                <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div class="flex justify-between items-center mb-6 border-b pb-4">
                        <div>
                            <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <span class="material-symbols-outlined text-yellow-500">trophy</span>
                                Weekly Hall of Fame (명예의 전당)
                            </h2>
                            <p class="text-xs text-gray-500 mt-1">
                                이번 주 활동 우수 유저 및 작품 (Cloud Function 자동 집계)
                            </p>
                        </div>
                        <button id="btn-refresh-hof" class="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-200 transition-colors flex items-center gap-2">
                             <span class="material-symbols-outlined text-sm">refresh</span> 새로고침
                        </button>
                    </div>
                    
                    <div id="hof-list" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                         <div class="text-center py-8 text-gray-400 col-span-3">
                            <span class="material-symbols-outlined animate-spin">sync</span> Loading...
                         </div>
                    </div>
                </div>

                <!-- 3. Advanced Curation Filter -->
                <div class="bg-gradient-to-br from-indigo-50 to-violet-50 p-6 rounded-xl shadow-inner border border-indigo-100">
                    <div class="mb-6">
                        <h2 class="text-xl font-bold text-indigo-900 flex items-center gap-2">
                             <span class="material-symbols-outlined text-indigo-500">search_check</span>
                            Advanced Discovery (고급 필터)
                        </h2>
                        <p class="text-xs text-indigo-500 mt-1">다양한 조건으로 숨겨진 보석을 발굴하세요.</p>
                    </div>

                    <!-- Filter Controls -->
                    <div class="bg-white p-4 rounded-xl border border-indigo-100 mb-6 shadow-sm">
                        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <div class="space-y-1">
                                <label class="text-xs font-bold text-gray-500">최소 조회수</label>
                                <input type="number" id="filter-min-views" class="w-full text-sm border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all outline-none" placeholder="0">
                            </div>
                            <div class="space-y-1">
                                <label class="text-xs font-bold text-gray-500">최소 좋아요</label>
                                <input type="number" id="filter-min-likes" class="w-full text-sm border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-pink-200 focus:border-pink-500 transition-all outline-none" placeholder="0">
                            </div>
                            <div class="space-y-1">
                                <label class="text-xs font-bold text-gray-500">최소 댓글</label>
                                <input type="number" id="filter-min-comments" class="w-full text-sm border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all outline-none" placeholder="0">
                            </div>
                            <div class="space-y-1">
                                <label class="text-xs font-bold text-gray-500">최소 평점 (1.0~5.0)</label>
                                <input type="number" id="filter-min-rating" step="0.1" max="5" class="w-full text-sm border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-yellow-200 focus:border-yellow-500 transition-all outline-none" placeholder="0.0">
                            </div>
                        </div>

                        <div class="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <!-- Logic Toggle -->
                            <div class="flex items-center gap-3 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                                <span class="text-xs font-bold text-gray-500">조합 조건:</span>
                                <label class="inline-flex items-center cursor-pointer">
                                    <input type="radio" name="filter-logic" value="AND" class="hidden peer" checked>
                                    <span class="px-3 py-1 text-xs font-bold rounded-md peer-checked:bg-indigo-600 peer-checked:text-white text-gray-400 hover:bg-gray-200 transition-all">AND (모두 만족)</span>
                                </label>
                                <label class="inline-flex items-center cursor-pointer">
                                    <input type="radio" name="filter-logic" value="OR" class="hidden peer">
                                    <span class="px-3 py-1 text-xs font-bold rounded-md peer-checked:bg-indigo-600 peer-checked:text-white text-gray-400 hover:bg-gray-200 transition-all">OR (하나라도)</span>
                                </label>
                            </div>

                            <div class="flex gap-2 w-full sm:w-auto">
                                <button id="btn-reset-filter" class="px-4 py-2 text-xs font-bold text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">초기화</button>
                                <button id="btn-apply-filter" class="flex-1 sm:flex-none px-6 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm hover:shadow transition-all flex items-center justify-center gap-2">
                                    <span class="material-symbols-outlined text-sm">filter_list</span> 조회하기
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Results Area -->
                    <div id="recommendation-grid" class="space-y-8">
                         <!-- Initial: Default Recommendations -->
                         <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Rising Stars -->
                            <div>
                                <h3 class="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1">
                                    <span class="material-symbols-outlined text-red-500 text-sm">trending_up</span> 급상승 (Rising)
                                </h3>
                                <div class="space-y-2">
                                    ${risingStars.length === 0 ? '<div class="text-xs text-gray-400">추천 데이터 부족</div>' : risingStars.map(s => renderCandidateCard(s)).join('')}
                                </div>
                            </div>
                            <!-- Hidden Gems -->
                             <div>
                                <h3 class="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1">
                                    <span class="material-symbols-outlined text-teal-500 text-sm">diamond</span> 숨은 명작 (Hidden Gem)
                                </h3>
                                <div class="space-y-2">
                                    ${hiddenGems.length === 0 ? '<div class="text-xs text-gray-400">추천 데이터 부족</div>' : hiddenGems.map(s => renderCandidateCard(s)).join('')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Hidden Input for File Upload -->
            <input type="file" id="banner-upload" class="hidden" accept="image/*">
        `;

        // --- View Functions ---
        function renderCandidateCard(s) {
            return `
                <div class="bg-white p-3 rounded-lg border border-indigo-100 shadow-sm flex justify-between items-center group hover:shadow-md transition-shadow">
                    <div class="flex items-center gap-3 overflow-hidden">
                        <div class="w-12 h-16 rounded bg-gray-200 overflow-hidden shrink-0">
                            <img src="${s.coverImage || ''}" class="w-full h-full object-cover">
                        </div>
                        <div class="min-w-0">
                            <div class="text-sm font-bold text-gray-900 truncate max-w-[180px]">${s.title}</div>
                            <div class="text-xs text-indigo-600 font-medium mb-0.5">${s.authorName}</div>
                            <div class="flex items-center gap-2 text-[10px] text-gray-400">
                                <span class="flex items-center gap-0.5"><span class="material-symbols-outlined text-[10px]">visibility</span> ${s.views || 0}</span>
                                <span class="flex items-center gap-0.5"><span class="material-symbols-outlined text-[10px]">favorite</span> ${s.likes || 0}</span>
                                <span class="flex items-center gap-0.5"><span class="material-symbols-outlined text-[10px]">chat_bubble</span> ${s.stats?.commentCount || 0}</span>
                                <span class="flex items-center gap-0.5 text-yellow-500 font-bold"><span class="material-symbols-outlined text-[10px] fill-current">star</span> ${(s.stats?.averageRating || 0).toFixed(1)}</span>
                            </div>
                        </div>
                    </div>
                    <button class="btn-quick-add bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white p-2 rounded-full transition-colors shrink-0" 
                        data-id="${s.id}" data-title="${s.title}" data-img="${s.coverImage || ''}">
                        <span class="material-symbols-outlined text-[20px]">add</span>
                    </button>
                </div>
            `;
        }

        // [Logic 1] Render Banners
        const bannerList = container.querySelector('#banner-list');
        if (banners.length > 0) {
            banners.forEach((b, idx) => {
                const card = document.createElement('div');
                card.className = 'relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50';
                card.innerHTML = `
                    <div class="aspect-video w-full bg-gray-200 relative">
                        <img src="${b.imageUrl}" class="w-full h-full object-cover">
                        <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button class="btn-banner-delete text-white p-2 hover:text-red-400 bg-white/10 rounded-full backdrop-blur-sm" data-id="${b.id}">
                                <span class="material-symbols-outlined">delete</span>
                            </button>
                        </div>
                        <div class="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded font-bold">#${idx + 1}</div>
                    </div>
                    <div class="p-3">
                        <input type="number" class="w-full text-xs p-1 border rounded mb-1 banner-order-input" value="${b.order || idx + 1}" data-id="${b.id}" placeholder="Order">
                        <div class="flex gap-1">
                            <input type="text" class="flex-1 text-xs p-1 border rounded banner-link-input" value="${b.linkUrl || ''}" data-id="${b.id}" placeholder="Link URL (/series/...)">
                            <button class="bg-gray-100 px-2 rounded border border-gray-200 hover:bg-indigo-100 hover:text-indigo-600 transition-colors btn-banner-pick-link" data-id="${b.id}" title="Select Series">
                                <span class="material-symbols-outlined text-[16px]">search</span>
                            </button>
                        </div>
                        <button class="w-full mt-2 bg-gray-900 text-white text-xs py-1 rounded hover:bg-black btn-banner-save" data-id="${b.id}">Update</button>
                    </div>
                `;
                bannerList.appendChild(card);
            });
        }

        // [Logic 2] Render Curation Table
        const curationList = container.querySelector('#curation-list');
        if (curatedSeries.length === 0) {
            curationList.innerHTML = '<tr><td colspan="4" class="text-center py-8 text-gray-400 text-sm">큐레이션된 작품이 없습니다.</td></tr>';
        } else {
            curatedSeries.forEach((s, idx) => {
                const row = document.createElement('tr');
                row.innerHTML = `
                    <td class="px-4 py-3 whitespace-nowrap">
                        <span class="text-lg font-black ${idx === 0 ? 'text-indigo-600' : 'text-gray-400'}">#${s.priority || idx + 1}</span>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap">
                        <div class="flex items-center">
                            <div class="h-10 w-10 flex-shrink-0">
                                <img class="h-10 w-10 rounded object-cover bg-gray-200" src="${s.coverImage || ''}">
                            </div>
                            <div class="ml-4">
                                <div class="text-sm font-medium text-gray-900 truncate max-w-[200px]">${s.title}</div>
                                <div class="text-xs text-gray-500">${s.authorName}</div>
                            </div>
                        </div>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap text-right">
                            <div class="flex items-center justify-end gap-1">
                            <button class="p-1 hover:bg-gray-100 rounded text-gray-500 btn-reorder" data-id="${s.id}" data-dir="up"><span class="material-symbols-outlined text-sm">arrow_upward</span></button>
                            <button class="p-1 hover:bg-gray-100 rounded text-gray-500 btn-reorder" data-id="${s.id}" data-dir="down"><span class="material-symbols-outlined text-sm">arrow_downward</span></button>
                        </div>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button class="text-red-500 hover:text-red-700 btn-remove-curation text-xs font-bold border border-red-100 hover:bg-red-50 px-3 py-1 rounded-full bg-white transition-colors" data-id="${s.id}">Remove</button>
                    </td>
                `;
                curationList.appendChild(row);
            });
        }

        // --- Logic Handlers (Using Service) ---

        // 1. Add Banner
        const fileInput = container.querySelector('#banner-upload');
        const btnAddBanner = container.querySelector('#btn-add-banner');

        btnAddBanner.onclick = () => {
            if (banners.length >= 5) {
                ModalManager.show(AlertModal, "제한 도달", "배너는 최대 5개까지만 등록할 수 있습니다.");
                return;
            }
            fileInput.click();
        };

        fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            btnAddBanner.disabled = true;
            btnAddBanner.textContent = 'Processing...';

            try {
                // Optimization (Keep here as it's Client-Side heavy)
                const { convertToWebP } = await import('../../../utils/image-processor.js');
                const webpBlob = await convertToWebP(file, { ratio: '16:9', maxWidth: 1600, quality: 0.85 });

                // Upload via Service
                await AdminCurationService.addBanner(webpBlob, banners.length + 1);

                CurationTab(container);

            } catch (err) {
                console.error(err);
                ModalManager.show(AlertModal, "오류", "배너 업로드 실패: " + err.message);
            } finally {
                btnAddBanner.disabled = false;
                btnAddBanner.textContent = '배너 추가';
                fileInput.value = '';
            }
        };

        // 2. Banner Actions (Update/Delete)
        const { SeriesPickerModal } = await import('../../../components/common/SeriesPickerModal.js');

        container.querySelectorAll('.btn-banner-pick-link').forEach(btn => {
            btn.onclick = () => {
                new SeriesPickerModal((series) => {
                    const input = container.querySelector(`.banner-link-input[data-id="${btn.dataset.id}"]`);
                    if (input) input.value = `/series/${series.id}`;
                }).render();
            };
        });

        container.querySelectorAll('.btn-banner-save').forEach(btn => {
            btn.onclick = async () => {
                const id = btn.dataset.id;
                const linkVal = container.querySelector(`.banner-link-input[data-id="${id}"]`).value;
                const orderVal = parseInt(container.querySelector(`.banner-order-input[data-id="${id}"]`).value);

                await AdminCurationService.updateBanner(id, { linkUrl: linkVal, order: orderVal });
                CurationTab(container);
            };
        });

        container.querySelectorAll('.btn-banner-delete').forEach(btn => {
            btn.onclick = async () => {
                const isConfirmed = await ModalManager.show(ConfirmModal, {
                    title: "배너 삭제",
                    message: "이 배너를 정말 삭제하시겠습니까?",
                    confirmText: "삭제",
                    type: 'danger'
                });

                if (isConfirmed) {
                    await AdminCurationService.deleteBanner(btn.dataset.id);
                    CurationTab(container);
                }
            };
        });

        // 3. Add Curation (Review Logic)
        const handleAddCuration = async (seriesId, title, img) => {
            console.log(`[UI] Requesting Add Curation: ${seriesId}`);

            const decodedTitle = decodeHtml(title);
            console.log(`[UI] Decoded Title: '${title}' -> '${decodedTitle}'`);

            const isConfirmed = await ModalManager.show(ConfirmModal, {
                title: "추천 등록",
                message: `'${title}' 작품을 추천 목록에 추가하시겠습니까?`,
                confirmText: "추가",
                type: 'info'
            });

            if (!isConfirmed) return;

            try {
                await AdminCurationService.addCuration(seriesId, curatedSeries.length + 1);
                console.log(`[UI] Add Curation Success. Refreshing...`);

                // Refresh First
                await CurationTab(container);

                // Ask to Post to Plaza
                const isPostConfirmed = await ModalManager.show(ConfirmModal, {
                    title: "썰화클럽 소개",
                    message: "📢 '썰화클럽'에 소개글도 함께 올리시겠습니까?\n(유저들에게 알림이 갈 수도 있습니다)",
                    confirmText: "등록",
                    type: 'info'
                });

                if (isPostConfirmed) {
                    try {
                        await AdminCurationService.postPlazaFeed({
                            type: 'SYSTEM_CURATION',
                            content: `✨ [MD Pick] ${decodedTitle}\n\n오늘의 추천작으로 선정되었습니다! 지금 바로 정주행해보세요. 👇`,
                            imageUrl: img,
                            linkUrl: `/series/${seriesId}`,
                            authorName: '썰화 월드 AI',
                            authorId: 'system'
                        });
                        ModalManager.show(AlertModal, "성공", "썰화클럽에 소개글이 등록되었습니다!");
                    } catch (e) {
                        ModalManager.show(AlertModal, "실패", "소개글 등록 실패: " + e.message);
                    }
                }

            } catch (e) {
                console.error("[UI] Curation Add Failed:", e);
                ModalManager.show(AlertModal, "오류", "추천 등록 중 오류가 발생했습니다: " + e.message);
            }
        };

        container.querySelector('#btn-add-curation').onclick = () => {
            new SeriesPickerModal(async (series) => {
                await handleAddCuration(series.id, series.title, series.coverImage);
            }).render();
        };

        // 4. Quick Add (From Recommendations)
        container.querySelectorAll('.btn-quick-add').forEach(btn => {
            btn.onclick = () => {
                handleAddCuration(btn.dataset.id, btn.dataset.title, btn.dataset.img);
            };
        });

        // 5. Remove Curation
        container.querySelectorAll('.btn-remove-curation').forEach(btn => {
            btn.onclick = async () => {
                const isConfirmed = await ModalManager.show(ConfirmModal, {
                    title: "추천 제거",
                    message: "추천 목록에서 제거하시겠습니까?",
                    confirmText: "제거",
                    type: 'danger'
                });

                if (isConfirmed) {
                    await AdminCurationService.removeCuration(btn.dataset.id);
                    CurationTab(container);
                }
            };
        });

        // 6. Reorder Curation
        container.querySelectorAll('.btn-reorder').forEach(btn => {
            btn.onclick = async () => {
                const id = btn.dataset.id;
                const dir = btn.dataset.dir;

                const currentIdx = curatedSeries.findIndex(s => s.id === id);
                if (currentIdx === -1) return;

                const targetIdx = dir === 'up' ? currentIdx - 1 : currentIdx + 1;
                if (targetIdx < 0 || targetIdx >= curatedSeries.length) return;

                const itemA = curatedSeries[currentIdx];
                const itemB = curatedSeries[targetIdx];

                await AdminCurationService.reorderCuration(itemA, itemB, targetIdx + 1, currentIdx + 1);

                // Auto Post Check for Top 1
                if ((targetIdx === 0 && dir === 'up') || (currentIdx === 0 && dir === 'down')) {
                    const newTop = dir === 'up' ? itemA : itemB;
                    const decodedTopTitle = decodeHtml(newTop.title);
                    console.log(`[UI] Decoded Top Title: '${newTop.title}' -> '${decodedTopTitle}'`);

                    const isConfirmed = await ModalManager.show(ConfirmModal, {
                        title: "1위 변경 감지",
                        message: `🎉 '${decodedTopTitle}' 작품이 1위가 되었습니다.\n'썰화클럽'에 축하 피드를 자동으로 올릴까요?`,
                        confirmText: "등록",
                        type: 'info'
                    });

                    if (isConfirmed) {
                        try {
                            await AdminCurationService.postPlazaFeed({
                                type: 'SYSTEM_CURATION',
                                content: `👑 [MD Pick] ${decodedTopTitle}\n\n지금 메인 화면에서 가장 핫한 작품으로 선정되었습니다! \n작가님 축하드립니다! 🥳`,
                                imageUrl: newTop.coverImage || newTop.thumbnail,
                                linkUrl: `/series/${newTop.id}`,
                                authorName: '썰화 월드 AI',
                                authorId: 'system'
                            });
                            ModalManager.show(AlertModal, "성공", "썰화클럽에 축하 피드가 발행되었습니다!");
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }

                CurationTab(container);
            };
        });

        // 7. Filter Application
        const applyFilter = () => {
            // ... (Filter Logic Same as before) ...
            const inputs = {
                minViews: parseInt(container.querySelector('#filter-min-views').value) || 0,
                minLikes: parseInt(container.querySelector('#filter-min-likes').value) || 0,
                minComments: parseInt(container.querySelector('#filter-min-comments').value) || 0,
                minRating: parseFloat(container.querySelector('#filter-min-rating').value) || 0
            };

            const isOrLogic = container.querySelector('input[name="filter-logic"]:checked').value === 'OR';

            const filtered = candidates.filter(s => {
                const views = s.views || 0;
                const likes = s.likes || 0;
                const comments = s.stats?.commentCount || 0;
                const rating = s.stats?.averageRating || 0;

                const checks = [];
                if (inputs.minViews > 0) checks.push(views >= inputs.minViews);
                if (inputs.minLikes > 0) checks.push(likes >= inputs.minLikes);
                if (inputs.minComments > 0) checks.push(comments >= inputs.minComments);
                if (inputs.minRating > 0) checks.push(rating >= inputs.minRating);

                if (isOrLogic) {
                    if (checks.length === 0) return true;
                    return checks.some(r => r === true);
                } else {
                    return views >= inputs.minViews && likes >= inputs.minLikes &&
                        comments >= inputs.minComments && rating >= inputs.minRating;
                }
            }).slice(0, 20);

            const grid = container.querySelector('#recommendation-grid');
            grid.innerHTML = filtered.length === 0
                ? `<div class="col-span-full py-10 text-center text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                     <span class="block mb-2">🔍</span> 조건에 맞는 작품이 없습니다.
                   </div>`
                : filtered.map(s => renderCandidateCard(s)).join('');

            // Re-bind buttons in new grid
            container.querySelectorAll('.btn-quick-add').forEach(btn => {
                btn.onclick = () => handleAddCuration(btn.dataset.id, btn.dataset.title, btn.dataset.img);
            });
        };

        container.querySelector('#btn-apply-filter').onclick = applyFilter;
        container.querySelector('#btn-reset-filter').onclick = () => {
            container.querySelectorAll('input[type="number"]').forEach(input => input.value = '');
            // Reset by reloading or just re-applying empty filter
            CurationTab(container);
        };

        // ----------------------------------------------------
        // [Logic 8] Weekly Hall of Fame (Added)
        // ----------------------------------------------------
        const loadHallOfFame = async () => {
            const hofList = container.querySelector('#hof-list');
            try {
                // Dynamic Import to avoid top-level dependency if possible, or just use AdminService
                const { AdminService } = await import('../../../services/admin.service.js');

                hofList.innerHTML = '<div class="text-center py-8 text-gray-400 col-span-3"><span class="material-symbols-outlined animate-spin">sync</span> Loading HOF...</div>';

                // Fetch Data
                const hofData = await AdminService.runWeeklyHallOfFame();

                if (!hofData || hofData.length === 0) {
                    hofList.innerHTML = '<div class="text-center py-4 text-gray-400 col-span-3">이번 주 명예의 전당 데이터가 없습니다. (집계 전)</div>';
                    return;
                }

                // Render
                hofList.innerHTML = hofData.map(item => `
                    <div class="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center gap-4">
                        <div class="relative w-12 h-12 shrink-0">
                             <img src="${item.displayImage || '/assets/default_avatar.png'}" class="w-full h-full object-cover rounded-full border border-white shadow-sm">
                             <div class="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center text-[10px] font-black text-white shadow">
                                ${item.rank || '-'}
                             </div>
                        </div>
                        <div class="min-w-0">
                            <div class="text-sm font-bold text-gray-900 truncate">${item.title || item.authorName || 'User'}</div>
                            <div class="text-xs text-gray-500">${item.type === 'user' ? '작가/유저' : '작품'}</div>
                            <div class="flex gap-2 mt-1">
                                <span class="bg-yellow-100 text-yellow-700 text-[10px] px-1.5 py-0.5 rounded font-bold">
                                    ${(item.score || 0).toLocaleString()} pts
                                </span>
                            </div>
                        </div>
                    </div>
                `).join('');

            } catch (e) {
                console.warn("HOF Load Error:", e);
                hofList.innerHTML = '<div class="text-center py-4 text-red-400 col-span-3">Load Failed</div>';
            }
        };

        // Initial Load
        loadHallOfFame();

        // Refresh Button
        container.querySelector('#btn-refresh-hof').onclick = () => {
            loadHallOfFame();
        };


    } catch (e) {
        console.error(e);
        container.innerHTML = `<div class="p-10 text-center text-red-500">Curation Service Error: ${e.message}</div>`;
    }
};
